import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api, PHP_VERSIONS } from '../lib/api'
import { Card, Button, Spinner, Badge, Checkbox, Field, Input } from '../components/ui'

interface NodeDefaults {
  install_nginx: boolean
  install_apache: boolean
  install_php: boolean
  php_versions: string[]
  install_mysql: boolean
  install_sftp: boolean
  install_certbot: boolean
  install_postfix: boolean
  install_dovecot: boolean
  install_docker: boolean
  install_redis: boolean
  install_neon: boolean
  install_fail2ban: boolean
  install_proftpd: boolean
}

type Tab = 'auto' | 'daemons' | 'vhost' | 'suspended' | 'system'
type DaemonKey =
  | 'nginx_main' | 'apache_ports'
  | 'mysql' | 'sftp'
  | 'php_fpm_pool' | 'php_ini_overrides' | 'php_extensions'
  | 'sysctl' | 'cgroups' | 'fail2ban' | 'ddos'

const DAEMON_TABS: {k: DaemonKey; label: string; hint: string}[] = [
  { k: 'nginx_main',        label: 'Nginx (main)',    hint: '/etc/nginx/conf.d/beebro.conf — глобальный include vhost’ов, gzip, hash-таблицы' },
  { k: 'apache_ports',      label: 'Apache (ports)',  hint: '/etc/apache2/ports.conf — на чём слушать apache (по-умолчанию 127.0.0.1:8080)' },
  { k: 'mysql',             label: 'MySQL',           hint: '/etc/mysql/mysql.conf.d/beebro.cnf — innodb_buffer_pool_size, max_connections и т.д.' },
  { k: 'sftp',              label: 'SFTP (sshd)',     hint: '/etc/ssh/sshd_config.d/beebro-sftp.conf — chroot блок для группы beebro-sftp' },
  { k: 'php_fpm_pool',      label: 'PHP-FPM pool',    hint: 'Шаблон /etc/php/<v>/fpm/pool.d/beebro-<user>.conf — на клиента' },
  { k: 'php_ini_overrides', label: 'php.ini defaults',hint: '/etc/php/<v>/fpm/conf.d/99-beebro.ini — memory_limit, timezone и т.д.' },
  { k: 'php_extensions',    label: 'PHP extensions',  hint: 'Список расширений (через пробел) — ставятся для каждой PHP-версии при её установке' },
  { k: 'sysctl',            label: 'Sysctl',          hint: '/etc/sysctl.d/99-beebro.conf — сетевые буферы, TCP-тюнинг, файловые дескрипторы' },
  { k: 'cgroups',           label: 'Cgroups (лимиты клиентов)', hint: 'CPU/RAM лимиты на каждого клиента через systemd user-<uid>.slice' },
  { k: 'fail2ban',          label: 'Fail2ban',        hint: '/etc/fail2ban/jail.d/beebro.conf — шаблон jails (sshd, nginx-http-auth, nginx-limit-req)' },
  { k: 'ddos',              label: 'DDoS защита',   hint: 'nginx limit_req + ipset+iptables DROP' },
]

export function SettingsPage() {
  const [tab, setTab] = useState<Tab>('auto')
  return (
    <div className="max-w-3xl">
      <h1 className="text-[22px] font-semibold mb-1">Настройки</h1>
      <p className="text-[13px] text-neutral-500 mb-5">
        Глобальные настройки панели — автоустановка на новых нодах и шаблоны vhost.
      </p>
      <div className="flex gap-1 mb-5 border-b border-neutral-200">
        {([
          ['auto',      'Автоустановка'],
          ['daemons',   'Конфиги демонов'],
          ['vhost',     'Шаблоны vhost'],
          ['suspended', 'Страница-заглушка'],
          ['system',    'О системе'],
        ] as [Tab, string][]).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-3.5 py-2 text-[13px] font-medium border-b-2 -mb-px transition ${
              tab === id ? 'border-neutral-900 text-neutral-900' : 'border-transparent text-neutral-500 hover:text-neutral-800'
            }`}>{label}</button>
        ))}
      </div>
      {tab === 'auto'      && <AutoInstallTab />}
      {tab === 'daemons'   && <DaemonsTab />}
      {tab === 'vhost'     && <VhostTemplatesTab />}
      {tab === 'suspended' && <SuspendedTab />}
      {tab === 'system'    && <SystemTab />}
    </div>
  )
}

function AutoInstallTab() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'node-defaults'],
    queryFn: async () => (await api.get<NodeDefaults>('/settings/node-defaults')).data,
  })
  const [form, setForm] = useState<NodeDefaults | null>(null)
  const [saved, setSaved] = useState(false)
  useEffect(() => { if (data) setForm(data) }, [data])

  const save = useMutation({
    mutationFn: async (d: NodeDefaults) => (await api.put('/settings/node-defaults', d)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'node-defaults'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    },
  })

  if (isLoading || !form) return <div className="p-8"><Spinner /></div>

  const set = <K extends keyof NodeDefaults>(k: K, v: NodeDefaults[K]) =>
    setForm({ ...form, [k]: v })

  const togglePHP = (v: string) => {
    const has = form.php_versions.includes(v)
    set('php_versions', has ? form.php_versions.filter(x => x !== v) : [...form.php_versions, v].sort())
  }

  return (
    <>
      <Card className="p-5 mb-5">
        <h2 className="text-[15px] font-semibold mb-1">Автоустановка на новых нодах</h2>
        <div className="text-[12px] text-neutral-500 mb-4">Когда добавляешь новую ноду — мастер сам ставит выбранное в фоне.</div>
        <div className="space-y-3">
          <Row label="Nginx" checked={form.install_nginx} onChange={v => set('install_nginx', v)}
            hint="HTTP-сервер для сайтов" />

          <Row label="Apache" checked={form.install_apache} onChange={v => set('install_apache', v)}
            hint="apache2 на 127.0.0.1:8080 (nginx проксирует PHP-запросы в него)" />

          <Row label="PHP-FPM" checked={form.install_php} onChange={v => set('install_php', v)}
            hint="Обработчик PHP-скриптов через FastCGI" />

          {form.install_php && (
            <div className="pl-6 -mt-1">
              <div className="text-[12px] text-neutral-500 mb-2">Версии PHP:</div>
              <div className="flex flex-wrap gap-2">
                {PHP_VERSIONS.map(v => {
                  const on = form.php_versions.includes(v)
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => togglePHP(v)}
                      className={`px-2.5 py-1 rounded-md text-[12px] border transition ${
                        on ? 'bg-black text-white border-black' : 'bg-white border-neutral-300 text-neutral-600 hover:border-neutral-400'
                      }`}
                    >
                      PHP {v}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          <Row label="MySQL" checked={form.install_mysql} onChange={v => set('install_mysql', v)}
            hint="СУБД для баз данных сайтов" />

          <Row label="SFTP (OpenSSH chroot)" checked={form.install_sftp} onChange={v => set('install_sftp', v)}
            hint="Изолированный доступ по SFTP каждому клиенту" />

          <Row label="Certbot (Let's Encrypt)" checked={form.install_certbot} onChange={v => set('install_certbot', v)}
            hint="Автоматическое получение SSL-сертификатов" />

          <div className="pt-3 border-t border-neutral-100">
            <div className="text-[12px] text-neutral-500 mb-2">Дополнительно:</div>
            <div className="space-y-3">
              <Row label="Docker" checked={form.install_docker} onChange={v => set('install_docker', v)} />
              <Row label="Redis" checked={form.install_redis} onChange={v => set('install_redis', v)} />
              <Row label="Neon (self-hosted Postgres)" checked={form.install_neon} onChange={v => set('install_neon', v)}
                hint="Native PostgreSQL cluster 'beebro' на 127.0.0.1:5433 (рядом с системным PG)" />
              <Row label="Fail2ban" checked={form.install_fail2ban} onChange={v => set('install_fail2ban', v)}
                hint="Защита от брутфорса sshd/nginx-auth/nginx-limit-req" />
              <Row label="ProFTPD (полный FTP)" checked={form.install_proftpd} onChange={v => set('install_proftpd', v)}
                hint="Протокол FTP (порт 21). Альтернатива/дополнение к SFTP." />
              <Row label="Postfix" checked={form.install_postfix} onChange={v => set('install_postfix', v)}
                hint="Почтовый MTA (отправка почты сайтами)" />
              <Row label="Dovecot" checked={form.install_dovecot} onChange={v => set('install_dovecot', v)}
                hint="IMAP/POP3 для почтовых ящиков" />
            </div>
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-neutral-100 flex items-center gap-3">
          <Button onClick={() => save.mutate(form)} disabled={save.isPending}>
            {save.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button>
          {saved && <Badge tone="green">Сохранено</Badge>}
        </div>
      </Card>
    </>
  )
}

function Row({ label, hint, checked, onChange }: {
  label: string
  hint?: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer select-none group">
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-neutral-300 accent-black cursor-pointer"
      />
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-neutral-800 group-hover:text-black">{label}</div>
        {hint && <div className="text-[12px] text-neutral-500">{hint}</div>}
      </div>
    </label>
  )
}

function TemplateTab({ name, title, hint, applyToAll }: { name: string; title: string; hint?: string; applyToAll?: boolean }) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'tpl', name],
    queryFn: async () => (await api.get<{ template: string }>(`/settings/templates/${name}`)).data,
  })
  const [text, setText] = useState<string>('')
  const [saved, setSaved] = useState(false)
  useEffect(() => { if (data) setText(data.template) }, [data])

  const save = useMutation({
    mutationFn: async () => (await api.put(`/settings/templates/${name}`, { template: text })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'tpl', name] })
      setSaved(true); setTimeout(() => setSaved(false), 2000)
    },
  })

  if (isLoading) return <div className="p-8"><Spinner /></div>

  return (
    <>
      <Card className="p-5 mb-5">
        <h2 className="text-[15px] font-semibold mb-1">{title}</h2>
        {hint && <div className="text-[12px] text-neutral-500 mb-3">{hint}</div>}
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          spellCheck={false}
          className="w-full h-[420px] font-mono text-[12px] leading-[1.5] p-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-900"
        />
        <div className="mt-4 flex items-center gap-3">
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button>
          <Button variant="secondary" onClick={() => data && setText(data.template)}>Отменить</Button>
          {applyToAll && (
            <Button variant="secondary" onClick={async () => {
              const nodes = (await api.get<{id:string;name:string}[]>('/nodes')).data
              let ok = 0
              for (const n of nodes) {
                try {
                  if (name === 'sysctl') await api.post(`/nodes/${n.id}/apply-sysctl`)
                  else if (name === 'fail2ban') await api.post(`/nodes/${n.id}/f2b/install`)
                  ok++
                } catch {}
              }
              alert(`Запущено на ${ok}/${nodes.length} нодах`)
            }}>Применить на всех нодах</Button>
          )}
          {saved && <Badge tone="green">Сохранено</Badge>}
        </div>
      </Card>
      <Card className="p-5 bg-neutral-50/50 border-dashed">
        <div className="text-[13px] text-neutral-600">
          Правки применятся к <b>новым сайтам</b> и к пересборке vhost'а. Существующие сайты
          с ручным override оставят свой конфиг (см. вкладку «Конфиг» на странице сайта).
        </div>
      </Card>
    </>
  )
}

function SuspendedTab() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'suspended-page'],
    queryFn: async () => (await api.get<{ html: string }>('/settings/suspended-page')).data,
  })
  const [html, setHtml] = useState('')
  const [saved, setSaved] = useState(false)
  const [preview, setPreview] = useState(false)
  useEffect(() => { if (data) setHtml(data.html) }, [data])

  const save = useMutation({
    mutationFn: async () => (await api.put('/settings/suspended-page', { html })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'suspended-page'] })
      setSaved(true); setTimeout(() => setSaved(false), 2000)
    },
  })

  if (isLoading) return <div className="p-8"><Spinner /></div>

  return (
    <>
      <Card className="p-5 mb-5">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-[15px] font-semibold">HTML страницы для отключённых сайтов</h2>
          <button type="button" onClick={() => setPreview(!preview)}
            className="text-[12px] text-neutral-500 hover:text-neutral-900 underline">
            {preview ? 'Показать код' : 'Показать превью'}
          </button>
        </div>
        <div className="text-[12px] text-neutral-500 mb-3">
          Отдаётся с HTTP 503 когда сайт заморожен (или клиент). Работает и для nginx-обработчика и для apache через nginx-фронт.
        </div>
        {preview ? (
          <iframe title="preview" srcDoc={html}
            className="w-full h-[420px] border border-neutral-300 rounded-md bg-white" />
        ) : (
          <textarea value={html} onChange={e => setHtml(e.target.value)}
            spellCheck={false}
            className="w-full h-[420px] font-mono text-[12px] leading-[1.5] p-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-900" />
        )}
        <div className="mt-4 flex items-center gap-3">
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button>
          <Button variant="secondary" onClick={() => data && setHtml(data.html)}>Отменить</Button>
          {saved && <Badge tone="green">Сохранено</Badge>}
        </div>
      </Card>
      <Card className="p-5 bg-neutral-50/50 border-dashed">
        <div className="text-[13px] text-neutral-600">
          Изменения применятся при следующей заморозке/разморозке сайта или клиента.
          Файл кладётся в <code className="text-[11px] bg-neutral-100 px-1.5 py-0.5 rounded">/etc/nginx/beebro-suspended.html</code>.
        </div>
      </Card>
    </>
  )
}

function VhostTemplatesTab() {
  const [sub, setSub] = useState<'nginx' | 'apache'>('nginx')
  return (
    <>
      <div className="flex gap-1 mb-3 border-b border-neutral-200 -mt-2">
        {(['nginx','apache'] as const).map(k => (
          <button key={k} onClick={() => setSub(k)}
            className={`px-3 py-2 text-[13px] font-medium border-b-2 -mb-px transition ${
              sub===k ? 'border-neutral-900 text-neutral-900' : 'border-transparent text-neutral-500 hover:text-neutral-800'
            }`}>{k === 'nginx' ? 'Nginx vhost' : 'Apache vhost'}</button>
        ))}
      </div>
      {sub === 'nginx' && (
        <TemplateTab name="nginx" title="Шаблон Nginx vhost"
          hint="Дефолт при создании сайта. Переменные: {{.Domain}}, {{.Docroot}}, {{.UnixUser}}, {{.PHPVersion}}, {{if .PHPEnabled}}…{{end}}, {{range .Aliases}}…{{end}}" />
      )}
      {sub === 'apache' && (
        <TemplateTab name="apache" title="Шаблон Apache vhost"
          hint="Дефолт при создании сайта с handler apache / nginx-apache" />
      )}
    </>
  )
}

function DaemonsTab() {
  const [sub, setSub] = useState<string>(DAEMON_TABS[0].k)
  const active = DAEMON_TABS.find(t => t.k === sub)!
  return (
    <>
      <div className="flex gap-1 flex-wrap mb-3 border-b border-neutral-200 -mt-2 pb-0">
        {DAEMON_TABS.map(t => (
          <button key={t.k} onClick={() => setSub(t.k)}
            className={`px-3 py-2 text-[13px] font-medium border-b-2 -mb-px transition ${
              sub === t.k ? 'border-neutral-900 text-neutral-900' : 'border-transparent text-neutral-500 hover:text-neutral-800'
            }`}>{t.label}</button>
        ))}
      </div>
      {active.k === 'php_extensions'
        ? <PHPExtensionsTab />
        : active.k === 'cgroups'
          ? <CgroupsTab />
          : active.k === 'ddos'
            ? <DDoSTab />
            : <TemplateTab name={active.k} title={active.label} hint={active.hint} applyToAll={active.k === 'fail2ban' || active.k === 'sysctl'} />}
    </>
  )
}

interface PHPExtCat { name: string; group: string; desc: string }

function PHPExtensionsTab() {
  const qc = useQueryClient()
  const { data: catalog = [] } = useQuery({
    queryKey: ['php-ext-catalog'],
    queryFn: async () => (await api.get<PHPExtCat[]>('/settings/php-extensions-catalog')).data,
  })
  const { data: currentTpl, isLoading } = useQuery({
    queryKey: ['settings', 'tpl', 'php_extensions'],
    queryFn: async () => (await api.get<{ template: string }>('/settings/templates/php_extensions')).data,
  })
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [filter, setFilter] = useState('')
  const [saved, setSaved] = useState(false)
  useEffect(() => {
    if (currentTpl) setSelected(new Set(currentTpl.template.split(/\s+/).filter(Boolean)))
  }, [currentTpl])

  const save = useMutation({
    mutationFn: async () => (await api.put('/settings/templates/php_extensions', {
      template: Array.from(selected).sort().join(' '),
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'tpl', 'php_extensions'] })
      setSaved(true); setTimeout(() => setSaved(false), 2000)
    },
  })

  const toggle = (name: string) => {
    const s = new Set(selected)
    if (s.has(name)) s.delete(name); else s.add(name)
    setSelected(s)
  }

  if (isLoading) return <div className="p-8"><Spinner /></div>

  const grouped: Record<string, PHPExtCat[]> = {}
  const q = filter.toLowerCase()
  for (const ext of catalog) {
    if (q && !ext.name.includes(q) && !ext.desc.toLowerCase().includes(q)) continue
    ;(grouped[ext.group] = grouped[ext.group] || []).push(ext)
  }
  const GROUP_RU: Record<string, string> = {
    runtime: 'Среда выполнения', database: 'Базы данных', text: 'Строки / XML / JSON',
    network: 'Сеть / HTTP', image: 'Изображения', archive: 'Архивы',
    math: 'Математика', crypto: 'Крипто', cache: 'Кэши / Очереди', misc: 'Другое',
  }

  return (
    <>
      <Card className="p-5 mb-5">
        <div className="flex items-start justify-between mb-1">
          <div>
            <h2 className="text-[15px] font-semibold">PHP extensions</h2>
            <div className="text-[12px] text-neutral-500">Ставятся для каждой PHP-версии при её установке. Выбрано: <b>{selected.size}</b></div>
          </div>
          <input placeholder="Поиск…" value={filter} onChange={e => setFilter(e.target.value)}
            className="px-3 py-1.5 text-[13px] border border-neutral-300 rounded-md w-[220px] focus:outline-none focus:ring-2 focus:ring-neutral-900" />
        </div>

        <div className="space-y-4 mt-4 max-h-[540px] overflow-y-auto pr-2">
          {Object.keys(grouped).sort().map(g => (
            <div key={g}>
              <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2 sticky top-0 bg-white py-1">{GROUP_RU[g] || g}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {grouped[g].map(ext => {
                  const on = selected.has(ext.name)
                  return (
                    <label key={ext.name}
                      className={`flex items-start gap-2 px-2 py-1.5 rounded-md cursor-pointer transition border ${
                        on ? 'border-neutral-900 bg-neutral-50' : 'border-transparent hover:bg-neutral-50'
                      }`}>
                      <input type="checkbox" checked={on} onChange={() => toggle(ext.name)}
                        className="mt-0.5 h-4 w-4 rounded border-neutral-300 accent-black cursor-pointer" />
                      <div className="min-w-0">
                        <div className="text-[13px] font-mono font-medium leading-tight">{ext.name}</div>
                        <div className="text-[11px] text-neutral-500 leading-tight mt-0.5 truncate">{ext.desc}</div>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-3 border-t border-neutral-100 flex items-center gap-3">
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button>
          <Button variant="secondary" onClick={() => currentTpl && setSelected(new Set(currentTpl.template.split(/\s+/).filter(Boolean)))}>Отменить</Button>
          {saved && <Badge tone="green">Сохранено</Badge>}
        </div>
      </Card>
    </>
  )
}

interface AgentVersionInfo {
  latest: string
  outdated: number
  total: number
  nodes: { node_id: string; name: string; current: string; outdated: boolean; online: boolean }[]
}

function SystemTab() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['agent-version'],
    queryFn: async () => (await api.get<AgentVersionInfo>('/agent-version')).data,
    refetchInterval: 15000,
  })
  const [running, setRunning] = useState(false)

  const updateAll = async () => {
    if (!confirm(`Обновить агент на всех устаревших нодах (${data?.outdated})?`)) return
    setRunning(true)
    try {
      await api.post('/agent-version/update-all')
      alert('Обновление запущено в фоне. Ноды переподключатся через ~10 сек.')
      setTimeout(() => qc.invalidateQueries({ queryKey: ['agent-version'] }), 15000)
    } finally { setRunning(false) }
  }

  if (isLoading || !data) return <div className="p-8"><Spinner /></div>

  return (
    <>
      <Card className="p-5 mb-5">
        <h2 className="text-[15px] font-semibold mb-1">Версия агента</h2>
        <div className="text-[12px] text-neutral-500 mb-4">Последняя доступная версия и статус агентов на нодах.</div>

        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="p-4 border border-neutral-200 rounded-md bg-neutral-50/40">
            <div className="text-[11px] uppercase text-neutral-500 tracking-wider">Latest</div>
            <div className="text-[18px] font-mono font-semibold mt-1">{data.latest}</div>
          </div>
          <div className="p-4 border border-neutral-200 rounded-md bg-neutral-50/40">
            <div className="text-[11px] uppercase text-neutral-500 tracking-wider">Всего нод</div>
            <div className="text-[18px] font-semibold mt-1">{data.total}</div>
          </div>
          <div className={`p-4 border rounded-md ${data.outdated > 0 ? 'border-yellow-300 bg-yellow-50' : 'border-neutral-200 bg-neutral-50/40'}`}>
            <div className="text-[11px] uppercase text-neutral-500 tracking-wider">Устарело</div>
            <div className={`text-[18px] font-semibold mt-1 ${data.outdated > 0 ? 'text-yellow-800' : ''}`}>{data.outdated}</div>
          </div>
        </div>

        {data.outdated > 0 && (
          <Button onClick={updateAll} disabled={running}>
            {running ? 'Запускаем…' : `↻ Обновить все ноды (${data.outdated})`}
          </Button>
        )}
      </Card>

      <Card className="overflow-hidden">
        <div className="px-5 py-3 border-b border-neutral-100">
          <h2 className="text-[15px] font-semibold">Ноды</h2>
        </div>
        <table className="w-full text-[13px]">
          <thead className="bg-neutral-50/60 border-b border-neutral-100 text-[11px] uppercase text-neutral-500 tracking-wider">
            <tr>
              <th className="text-left font-medium px-5 py-2">Имя</th>
              <th className="text-left font-medium px-4 py-2">Версия</th>
              <th className="text-left font-medium px-4 py-2">Статус</th>
              <th className="text-right font-medium px-5 py-2 w-[140px]">Действия</th>
            </tr>
          </thead>
          <tbody>
            {data.nodes.map(n => (
              <tr key={n.node_id} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/60">
                <td className="px-5 py-3 font-medium">{n.name}</td>
                <td className="px-4 py-3">
                  <span className="font-mono text-[13px]">{n.current || '—'}</span>
                  {n.outdated && <Badge tone="yellow">устарел</Badge>}
                  {!n.outdated && n.current && <span className="ml-2 text-[12px] text-green-700">✓</span>}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[12px] ${n.online ? 'text-green-700' : 'text-neutral-400'}`}>
                    {n.online ? 'онлайн' : 'офлайн'}
                  </span>
                </td>
                <td className="px-5 py-3 text-right">
                  {n.outdated && (
                    <Button size="sm" variant="secondary" onClick={async () => {
                      await api.post(`/nodes/${n.node_id}/agent-update`)
                      alert('Запущено. Обновится через ~10 сек.')
                    }}>Обновить</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  )
}

interface CGroupsDefaults {
  enabled: boolean
  cpu_quota_percent: number
  memory_max_mb: number
  tasks_max: number
}

function CgroupsTab() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'cgroups'],
    queryFn: async () => (await api.get<CGroupsDefaults>('/settings/cgroups')).data,
  })
  const [f, setF] = useState<CGroupsDefaults | null>(null)
  const [saved, setSaved] = useState(false)
  useEffect(() => { if (data) setF(data) }, [data])

  const save = useMutation({
    mutationFn: async () => (await api.put('/settings/cgroups', f)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'cgroups'] })
      setSaved(true); setTimeout(() => setSaved(false), 2000)
    },
  })

  if (isLoading || !f) return <div className="p-8"><Spinner /></div>

  return (
    <>
      <Card className="p-5 mb-5">
        <h2 className="text-[15px] font-semibold mb-1">Cgroups — лимиты клиентов</h2>
        <div className="text-[12px] text-neutral-500 mb-4">
          Ограничения CPU/RAM для каждого клиента через systemd <code>user-&lt;uid&gt;.slice</code>.
          Эти значения — <b>дефолт для новых клиентов</b>. У каждого клиента можно переопределить.
        </div>

        <Checkbox label="Включить cgroups-лимиты" checked={f.enabled}
                  onChange={e => setF({ ...f, enabled: e.target.checked })} />

        {f.enabled && (
          <div className="mt-4 space-y-3 pl-6 border-l-2 border-neutral-100">
            <Field label="CPU лимит (%)" hint="100 = 1 ядро, 200 = 2 ядра, 0 = без лимита">
              <Input type="number" value={f.cpu_quota_percent}
                     onChange={e => setF({ ...f, cpu_quota_percent: +e.target.value })} />
            </Field>
            <Field label="RAM максимум (MB)" hint="0 = без лимита. Пример: 2048 = 2 GB на клиента.">
              <Input type="number" value={f.memory_max_mb}
                     onChange={e => setF({ ...f, memory_max_mb: +e.target.value })} />
            </Field>
            <Field label="Макс. процессов (TasksMax)" hint="Защита от fork-бомб. По умолчанию 4096.">
              <Input type="number" value={f.tasks_max}
                     onChange={e => setF({ ...f, tasks_max: +e.target.value })} />
            </Field>
          </div>
        )}

        <div className="mt-5 pt-4 border-t border-neutral-100 flex items-center gap-3">
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button>
          {saved && <Badge tone="green">Сохранено</Badge>}
        </div>
      </Card>

      <Card className="p-5 bg-neutral-50/50 border-dashed">
        <div className="text-[13px] text-neutral-600">
          <b>Как это работает.</b> При изменении настроек новые клиенты создаются с этими лимитами.
          Существующим клиентам можно переопределить через <code>💾</code> в списке клиентов
          (или задать индивидуально в API <code>POST /clients/&#123;id&#125;/cgroups</code>).
          Требует cgroups v2 (Ubuntu 22+).
        </div>
      </Card>
    </>
  )
}

interface DDoSDefaults {
  enabled: boolean
  rate_per_second: number
  burst: number
  ban_seconds: number
}

function DDoSTab() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'ddos'],
    queryFn: async () => (await api.get<DDoSDefaults>('/settings/ddos')).data,
  })
  const [f, setF] = useState<DDoSDefaults | null>(null)
  const [saved, setSaved] = useState(false)
  useEffect(() => { if (data) setF(data) }, [data])

  const save = useMutation({
    mutationFn: async () => (await api.put('/settings/ddos', f)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'ddos'] })
      setSaved(true); setTimeout(() => setSaved(false), 2000)
    },
  })

  if (isLoading || !f) return <div className="p-8"><Spinner /></div>

  return (
    <>
      <Card className="p-5 mb-5">
        <h2 className="text-[15px] font-semibold mb-1">DDoS защита</h2>
        <div className="text-[12px] text-neutral-500 mb-4">
          Ограничение частоты запросов через nginx <code>limit_req</code>.
          При превышении IP заносится в ipset <code>beebro_ddos_ban</code> и iptables DROPает пакеты.
        </div>

        <Checkbox label="Включить DDoS-защиту (ipset + iptables)"
                  checked={f.enabled} onChange={e => setF({ ...f, enabled: e.target.checked })} />

        {f.enabled && (
          <div className="mt-4 space-y-3 pl-6 border-l-2 border-neutral-100">
            <Field label="Запросов в секунду с одного IP" hint="Норма 25-50. Всё что выше — попадает под лимит.">
              <Input type="number" value={f.rate_per_second}
                     onChange={e => setF({ ...f, rate_per_second: +e.target.value })} />
            </Field>
            <Field label="Burst (пиковый всплеск)" hint="Сколько запросов подряд разрешено сверх rate.">
              <Input type="number" value={f.burst}
                     onChange={e => setF({ ...f, burst: +e.target.value })} />
            </Field>
            <Field label="Длительность бана (секунд)" hint="Сколько держим IP в ipset.">
              <Input type="number" value={f.ban_seconds}
                     onChange={e => setF({ ...f, ban_seconds: +e.target.value })} />
            </Field>
          </div>
        )}

        <div className="mt-5 pt-4 border-t border-neutral-100 flex items-center gap-3">
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Сохраняем…' : 'Сохранить и применить на всех нодах'}
          </Button>
          {saved && <Badge tone="green">Применено</Badge>}
        </div>
      </Card>
    </>
  )
}
